import React, { useEffect, useMemo, useState, createElement } from 'react';
import { Role, ViewType, ModalType, Loan, ColumnKey, ColumnScope, Note, MOCK_LOANS, SCOPE_COLUMNS, TODAY, COLUMN_LABELS } from './data';
import { Header, ViewControls, AlertCards, FiltersBar, FilterKey, FilterState } from './components/DashboardControls';
import { PipelineTable } from './components/PipelineTable';
import { Modals } from './components/Modals';
import { SOSMarkModal } from './components/SOSMarkModal';
import { ClearSOSModal } from './components/ClearSOSModal';
import { ExportModal } from './components/ExportModal';
const EMPTY_FILTERS: FilterState = {
  processor: [],
  processorAssistant: [],
  loanOwner: [],
  monthPushing: [],
  lockExpiration: [],
  scheduledClosing: [],
  scheduledFunding: []
};
const PAGE_SIZE = 100;
/** Identity used for note attribution, scoped views, etc. — follows the active role. */
const USER_BY_ROLE: Record<Role, string> = {
  Processor: 'Alex Chen',
  'Processor Assistant': 'Sam Smith',
  'Loan Owner': 'Mike Ross',
  'Team Lead': 'Jessica Pearson',
  Admin: 'Admin User'
};
function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean))).sort();
}
function nowTimeString() {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function App() {
  const [role, setRole] = useState<Role>('Processor');
  const [view, setView] = useState<ViewType>('My Pipeline');
  const [isDark, setIsDark] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [loans, setLoans] = useState<Loan[]>(MOCK_LOANS);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(nowTimeString());
  const [sortResetKey, setSortResetKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // SOS workflow state
  const [sosMarkTarget, setSosMarkTarget] = useState<Loan | null>(null);
  const [sosClearTarget, setSosClearTarget] = useState<Loan | null>(null);
  // Admin-configurable state
  const [alwaysIncludedRecipients, setAlwaysIncludedRecipients] = useState<string[]>(['compliance@activelink.com']);
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnScope, ColumnKey[]>>(SCOPE_COLUMNS);
  const [daysInPipeThresholds, setDaysInPipeThresholds] = useState<{
    good: number;
    warning: number;
  }>({
    good: 15,
    warning: 30
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);
  // Current user identity follows the role being previewed
  const currentUser = USER_BY_ROLE[role];
  // Admin sees a single all-in-process view; non-admin views switch
  const effectiveView: ViewType = role === 'Admin' ? 'All In Process' : view;
  const viewLoans = useMemo(() => {
    if (effectiveView === 'All In Process') return loans;
    if (effectiveView === 'My Pipeline') {
      // Role-aware "my" scope — loans where I am the personally responsible party.
      // For Team Lead, "My Pipeline" means loans they personally own (not the whole team).
      return loans.filter(l => {
        switch (role) {
          case 'Processor':
            return l.processor === currentUser;
          case 'Processor Assistant':
            return l.processorAssistant === currentUser;
          case 'Loan Owner':
            return l.loanOwner === currentUser;
          case 'Team Lead':
            return l.loanOwner === currentUser;
          default:
            return false;
        }
      });
    }
    // Team Pipeline — Team Lead sees only loans where they are the Team Lead
    if (role === 'Team Lead') {
      return loans.filter(l => l.teamLead === currentUser);
    }
    return loans;
  }, [loans, effectiveView, role, currentUser]);
  const filterOptions = useMemo<Record<FilterKey, string[]>>(() => ({
    processor: uniq(viewLoans.map(l => l.processor)),
    processorAssistant: uniq(viewLoans.map(l => l.processorAssistant)),
    loanOwner: uniq(viewLoans.map(l => l.loanOwner)),
    monthPushing: uniq(viewLoans.map(l => l.monthPushing)),
    lockExpiration: uniq(viewLoans.map(l => l.lockExpiration)),
    scheduledClosing: uniq(viewLoans.map(l => l.scheduledClosing)),
    scheduledFunding: uniq(viewLoans.map(l => l.scheduledFunding))
  }), [viewLoans]);
  const displayLoans = useMemo(() => {
    return viewLoans.filter(l => {
      if (filters.processor.length && !filters.processor.includes(l.processor)) return false;
      if (filters.processorAssistant.length && !filters.processorAssistant.includes(l.processorAssistant)) return false;
      if (filters.loanOwner.length && !filters.loanOwner.includes(l.loanOwner)) return false;
      if (filters.monthPushing.length && !filters.monthPushing.includes(l.monthPushing)) return false;
      if (filters.lockExpiration.length && !filters.lockExpiration.includes(l.lockExpiration)) return false;
      if (filters.scheduledClosing.length && !filters.scheduledClosing.includes(l.scheduledClosing)) return false;
      if (filters.scheduledFunding.length && !filters.scheduledFunding.includes(l.scheduledFunding)) return false;
      return true;
    });
  }, [viewLoans, filters]);
  useEffect(() => {
    setPage(1);
  }, [filters, view, role]);
  /** Map current role+view to the matching column scope. Team Lead has two distinct scopes. */
  const activeScope: ColumnScope = role === 'Admin' ? 'Admin' : role === 'Team Lead' ? view === 'My Pipeline' ? 'Team Lead — My Pipeline' : 'Team Lead — Team Pipeline' : role as ColumnScope;
  const activeRoleColumns = columnVisibility[activeScope];
  const alertCounts = useMemo(() => {
    return {
      newApp: displayLoans.filter(l => l.isNewApp).length,
      // SOS = explicit SOS + locks expired/today
      sos: displayLoans.filter(l => {
        if (l.isSos) return true;
        const lockDate = new Date(l.lockExpiration + 'T00:00:00Z');
        return lockDate.getTime() <= TODAY.getTime();
      }).length,
      // Locks Expiring Soon = within 15 days but not yet past
      rateLock: displayLoans.filter(l => {
        const lockDate = new Date(l.lockExpiration + 'T00:00:00Z');
        const days = Math.ceil((lockDate.getTime() - TODAY.getTime()) / (1000 * 60 * 60 * 24));
        return days >= 0 && days <= 15;
      }).length
    };
  }, [displayLoans]);
  const handleUpdateLoan = (id: string, field: keyof Loan, value: any) => {
    setLoans(prev => prev.map(loan => loan.id === id ? {
      ...loan,
      [field]: value
    } : loan));
  };
  /** Only Processor and Admin can clear an active SOS flag. */
  const canClearSos = role === 'Processor' || role === 'Admin';
  const handleToggleSos = (loan: Loan) => {
    if (loan.isSos) {
      if (!canClearSos) return;
      setSosClearTarget(loan);
    } else {
      setSosMarkTarget(loan);
    }
  };
  const handleConfirmMarkSos = (note: string, recipients: string[]) => {
    if (!sosMarkTarget) return;
    const newNote: Note = {
      id: `n-${Date.now()}`,
      text: note,
      author: currentUser,
      timestamp: new Date().toISOString(),
      kind: 'create'
    };
    const mergedRecipients = Array.from(new Set([...alwaysIncludedRecipients, ...recipients]));
    setLoans(prev => prev.map(l => l.id === sosMarkTarget.id ? {
      ...l,
      isSos: true,
      sosNote: newNote,
      sosRecipients: mergedRecipients,
      sosNoteHistory: [...l.sosNoteHistory, newNote]
    } : l));
    showToast(`SOS flagged — notification sent to ${mergedRecipients.length} recipient${mergedRecipients.length === 1 ? '' : 's'}`);
    setSosMarkTarget(null);
  };
  const handleConfirmClearSos = (clearanceNote: string) => {
    if (!sosClearTarget) return;
    const recipients = sosClearTarget.sosRecipients || alwaysIncludedRecipients;
    const note: Note = {
      id: `n-${Date.now()}`,
      text: clearanceNote,
      author: currentUser,
      timestamp: new Date().toISOString(),
      kind: 'clear'
    };
    setLoans(prev => prev.map(l => l.id === sosClearTarget.id ? {
      ...l,
      isSos: false,
      sosNote: undefined,
      sosRecipients: undefined,
      sosNoteHistory: [...l.sosNoteHistory, note]
    } : l));
    showToast(`SOS cleared — clearance email sent to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`);
    setSosClearTarget(null);
  };
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  };
  const handleRequestClearSos = (loan: Loan) => {
    setActiveModal(null);
    setSosClearTarget(loan);
  };
  const handleFilterChange = (key: FilterKey, value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleClearFilters = () => setFilters(EMPTY_FILTERS);
  const handleReset = () => {
    if (role !== 'Admin') setView('My Pipeline');
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setLastUpdated(nowTimeString());
    setSortResetKey(k => k + 1);
  };
  const handleRefresh = () => {
    setLastUpdated(nowTimeString());
  };
  /** Processors found in the current filtered pipeline, with loan counts. */
  const processorsWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    displayLoans.forEach(l => {
      if (!l.processor) return;
      counts.set(l.processor, (counts.get(l.processor) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [displayLoans]);
  /** Columns to include in the export. Uses Admin column config (which contains all relevant columns). */
  const exportColumns: ColumnKey[] = columnVisibility.Admin;
  /** Sanitize a string for use as an Excel sheet name (max 31 chars, no [ ] : * ? / \). */
  const safeSheetName = (raw: string, idx: number) => {
    let name = raw.replace(/[\[\]:*?/\\]/g, ' ').trim();
    if (name.length > 31) name = name.slice(0, 28) + `…${idx}`;
    return name || `Sheet ${idx + 1}`;
  };
  const cellValue = (l: Loan, c: ColumnKey): string | number => {
    switch (c) {
      case 'sos':
        return l.isSos ? 'Y' : 'N';
      case 'loanAmount':
        return l.loanAmount;
      case 'daysInPipe':
        return l.daysInPipe;
      case 'loanName':
        return l.loanName;
      case 'stage':
        return l.stage;
      case 'monthPushing':
        return l.monthPushing;
      case 'processorNote':
        return l.processorNote;
      case 'loanOwner':
        return l.loanOwner;
      case 'teamLead':
        return l.teamLead;
      case 'processor':
        return l.processor;
      case 'processorAssistant':
        return l.processorAssistant;
      default:
        return '';
    }
  };
  const handleExport = async (processors: string[]) => {
    if (processors.length === 0) {
      setActiveModal(null);
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      const headers = exportColumns.map(c => COLUMN_LABELS[c]);
      processors.forEach((proc, idx) => {
        const procLoans = displayLoans.filter(l => l.processor === proc);
        const rows = procLoans.map(l => exportColumns.map(c => cellValue(l, c)));
        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        // Column widths — auto-size based on header length, capped
        ws['!cols'] = exportColumns.map(c => ({
          wch: Math.min(Math.max(COLUMN_LABELS[c].length + 2, 12), 40)
        }));
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName(proc, idx));
      });
      const fileName = `pipeline-by-processor-${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      showToast(`Exported ${processors.length} processor sheet${processors.length === 1 ? '' : 's'} to ${fileName}`);
    } catch (err) {
      console.error('Export failed', err);
      showToast('Export failed — see console for details');
    }
    setActiveModal(null);
  };
  const viewLabel = role === 'Admin' ? `All Loans In Process · ${role} View` : `${view} · ${role} View`;
  // Modals component only handles the alert/admin overlays — export & sos modals
  // are rendered separately below so we filter activeModal accordingly.
  const modalForOverlay: ModalType = activeModal === 'export' ? null : activeModal;
  return <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900 transition-colors duration-200" data-id="element-0">
      <Header role={role} setRole={setRole} isDark={isDark} toggleDark={() => setIsDark(!isDark)} lastUpdated={lastUpdated} onReset={handleReset} onOpenAdmin={() => setActiveModal('admin')} onRefresh={handleRefresh} onOpenExport={() => setActiveModal('export')} data-id="element-1" />

      <main className="flex-1 flex flex-col p-4 space-y-4 max-w-[1600px] mx-auto w-full h-[calc(100vh-64px)]" data-id="element-2">
        <div className="flex flex-col space-y-4 flex-shrink-0" data-id="element-3">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-4">
            <div className="flex items-center gap-3" data-id="element-5">
              <ViewControls view={view} setView={setView} role={role} data-id="element-6" />
              <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline" data-id="element-7">
                {viewLabel}
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-8">
              <span className="font-medium text-slate-700 dark:text-slate-300" data-id="element-9">
                {displayLoans.length}
              </span>{' '}
              active {displayLoans.length === 1 ? 'loan' : 'loans'}
            </div>
          </div>

          <AlertCards onOpenModal={setActiveModal} counts={alertCounts} data-id="element-10" />

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 shadow-sm" data-id="element-11">
            <FiltersBar filters={filters} options={filterOptions} onChange={handleFilterChange} onClearAll={handleClearFilters} data-id="element-12" />
          </div>
        </div>

        <div className="flex-1 min-h-0" data-id="element-13">
          <PipelineTable loans={displayLoans} role={role} visibleColumns={activeRoleColumns} canClearSos={canClearSos} onUpdateLoan={handleUpdateLoan} onToggleSos={handleToggleSos} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sortResetKey={sortResetKey} daysInPipeThresholds={daysInPipeThresholds} data-id="element-14" />
        </div>
      </main>

      <Modals activeModal={modalForOverlay} onClose={() => setActiveModal(null)} loans={displayLoans} role={role} onRequestClearSos={handleRequestClearSos} alwaysIncludedRecipients={alwaysIncludedRecipients} setAlwaysIncludedRecipients={setAlwaysIncludedRecipients} columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} daysInPipeThresholds={daysInPipeThresholds} setDaysInPipeThresholds={setDaysInPipeThresholds} data-id="element-15" />

      {activeModal === 'export' && <ExportModal onClose={() => setActiveModal(null)} onExport={handleExport} rowCount={displayLoans.length} processorsWithCounts={processorsWithCounts} data-id="element-16" />}

      {sosMarkTarget && <SOSMarkModal loan={sosMarkTarget} alwaysIncludedRecipients={alwaysIncludedRecipients} onClose={() => setSosMarkTarget(null)} onConfirm={handleConfirmMarkSos} data-id="element-17" />}

      {sosClearTarget && <ClearSOSModal loan={sosClearTarget} onClose={() => setSosClearTarget(null)} onConfirm={handleConfirmClearSos} data-id="element-18" />}
      {/* Toast */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] bg-slate-900 dark:bg-slate-800 text-white text-sm px-4 py-2.5 rounded-lg shadow-xl border border-slate-700 flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200 max-w-sm" data-id="element-19">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" data-id="element-20" />
          <span data-id="element-21">{toast}</span>
        </div>}
    </div>;
}